/**
 * `nav` namespace — core cross-cutting strings for the app nav surface.
 * Populated as strings are externalized (ADR 0065 Phase 2).
 *
 * Source of truth: the FEATURES manifest in `src/chrome/features.tsx`. Each nav
 * entry carries a `labelKey`/`hintKey`; group headers carry a group→key map.
 * Consumers (Sidebar / AdminLayout / CommandPalette) resolve these via
 * `t(key, { defaultValue: englishFallback })`.
 */
export const messages = {
  siteNavLabel: 'KickTodo',
  siteNavMore: 'More',
  siteNavMoreLabel: 'More destinations',
  siteNavCurrent: '{{label}} page',
  // Group headers
  groupWorkspace: 'Workspace',
  groupCrm: 'CRM',
  groupSales: 'Sales',
  groupCommerce: 'Commerce',
  groupMarketing: 'Marketing',
  groupCdp: 'Customer Data Platform',
  groupStudio: 'Studio',
  groupPlanning: 'Planning',
  groupAdmin: 'Admin',
  groupWorkManagement: 'Work management',
  groupSystemOperations: 'System operations',
  groupWorkforces: 'Workforces',
  groupContent: 'Content',
  groupAiAutomation: 'AI & automation',
  groupGovernanceSecurity: 'Governance & security',
  groupPlatform: 'Platform',
  groupDeveloperTools: 'Developer tools',
  groupBusiness: 'Business',
  groupIdentityAccess: 'Identity & access',
  groupDataKnowledge: 'Data & knowledge',
  groupBillingCommerce: 'Billing & commerce',
  groupAnalyticsUsage: 'Analytics & usage',
  groupDeploymentCustomization: 'Deployment & customization',
  groupLearningSupport: 'Learning & support',
  groupActions: 'Actions',
  // Item labels + hints
  chatLabel: 'Chat',
  chatHint: 'Conversational entry point',
  agentsLabel: 'Agents',
  agentsHint: 'Your digital workforce — named AI coworkers',
  workflowsLabel: 'Workflows',
  workflowsHint: 'Author + edit workflows',
  overviewLabel: 'Overview',
  overviewHint: 'Admin home',
  runsLabel: 'Runs',
  walkthroughsLabel: 'Walkthroughs',
  walkthroughsHint: 'Record + play guided tours',
  opsHubLabel: 'Ops Console',
  opsHubHint: 'System health, DLQ, webhook + admin consoles',
  settingsShellLabel: 'Settings',
  settingsShellHint: 'Personal preferences — theme, accessibility, AI budget, privacy',
  runsHint: 'Execution history + detail',
  boardsLabel: 'Boards',
  boardsHint: 'Kanban — card → run trigger',
  workforcesLabel: 'Workforces',
  workforcesHint: 'Governed agent clusters — purpose, telemetry, autonomy',
  agentTemplatesLabel: 'Agent templates',
  agentTemplatesHint: 'Installed manifest agents + packs',
  orgChartLabel: 'Org chart',
  orgChartHint: 'Roster + org-chart editor (descriptive only — confers no authority)',
  promptsLabel: 'Prompts',
  promptsHint: 'Reusable templates + variables',
  memoryLabel: 'Memory',
  memoryHint: 'Summaries of completed runs',
  capabilitiesLabel: 'Capabilities',
  capabilitiesHint: 'What this host advertises',
  cliLabel: 'CLI',
  cliHint: 'In-app CLI quickstart + catalog',
  manualTestsLabel: 'Manual tests',
  manualTestsHint: 'Human-run feature tests',
  designSystemLabel: 'Design system',
  designSystemHint: 'Primitive + pattern gallery',
  accessHubLabel: 'Access & connections',
  accessHubHint: 'Credentials, connections & access in one place',
  modelsLabel: 'Models',
  modelsHint: 'Choose which model answers, and see which performs',
  chatDeploymentLabel: 'Always-on chat',
  chatDeploymentHint: 'Schedule it, or put it on your website',
  featureTogglesLabel: 'Feature toggles',
  featureTogglesHint: 'On / off / beta + multivariant traffic-splitting',
  agentAllowlistLabel: 'Agent tool allowlists',
  agentAllowlistHint: 'Grant or revoke an agent’s tools without editing a pack',
  exampleDataLabel: 'Example data',
  exampleDataHint: 'Re-seed the built-in example roster',
  // Command-palette quick actions
  advisoryBoardLabel: 'Board of Advisors',
  advisoryBoardHint: 'Councils of advisor agents',
  ambientWorkGraphLabel: 'Work patterns',
  ambientWorkGraphHint: 'Recurring work → suggested workflows',
  analyticsLabel: 'Analytics',
  analyticsHint: 'Traffic + conversions on the public surface',
  billingLabel: 'Billing',
  commerceLabel: 'Commerce',
  recommendationsLabel: 'Recommendations',
  recommendationsHint: 'Upsell, cross-sell & recommendations',
  promotionsLabel: 'Promotions',
  promotionsHint: 'Promotions & loss-leaders',
  funnelsLabel: 'Funnels',
  funnelsHint: 'Multi-step sales funnels',
  experimentsLabel: 'Experiments',
  experimentsHint: 'Pre-registered A/B tests',
  tutorialsLabel: 'Tutorials',
  tutorialsHint: 'Hands-on product walkthroughs',
  customDomainsLabel: 'Custom domains',
  customDomainsHint: 'Serve published content on your own hostname',
  discoveryLabel: 'Discovery',
  discoveryHint: 'Search, collections & merchandising',
  commerceHint: 'Products, orders, quotes & pricing',
  billingHint: 'Plan & token balance',
  commerceConnectLabel: 'Sell on marketplace',
  commerceConnectHint: 'Stripe Connect seller onboarding',
  brandLabel: 'Brand',
  brandHint: 'Define & enforce brand voice',
  campaignBriefLabel: 'Campaign Briefs',
  creativeBriefsLabel: 'Creative Briefs',
  creativeBriefsHint: 'Visual briefs for designers — scene, directions, mood board',
  campaignBriefHint: 'Personas, briefs & the messaging kernel',
  campaignConnectorsLabel: 'Performance',
  campaignConnectorsHint: 'Ad performance import & KPIs',
  campaignIntelLabel: 'Intelligence',
  campaignIntelHint: 'Budget & forecast recommendations',
  campaignOpsLabel: 'Operations',
  campaignOpsHint: 'Ask for, review and approve client page changes',
  campaignPlaybooksLabel: 'Playbooks',
  campaignPlaybooksHint: 'Roll out your campaign methods to every client',
  campaignOrchestrationLabel: 'Campaigns',
  campaignOrchestrationHint: 'Run & manage marketing campaigns',
  chatWidgetLabel: 'Chat widgets',
  chatWidgetHint: 'Embeddable chat widgets',
  cmsLabel: 'CMS',
  cmsHint: 'Pages + page builder',
  cmsTranslateLabel: 'Translations',
  cmsTranslateHint: 'Translate page content you were granted',
  commentsLabel: 'Comments',
  commentsHint: 'Threaded comments on pages + collections',
  commerceUcpLabel: 'UCP commerce',
  commerceUcpHint: 'Agentic-commerce endpoints & clients',
  ucpPurchasesLabel: 'Agent purchases',
  ucpPurchasesHint: 'Purchases agents placed over UCP',
  consentLabel: 'Consent',
  consentHint: 'Region-aware consent + data-subject (GDPR)',
  crmLabel: 'CRM',
  crmHint: 'Contacts + triage',
  cdpLabel: 'CDP',
  cdpHint: 'Resolve a customer by any identifier',
  csmLabel: 'CSM',
  csmHint: 'Customer-success accounts',
  entitiesLabel: 'Entities',
  entitiesHint: 'User-defined content types',
  environmentsLabel: 'Environments',
  environmentsHint: 'Config promotion & rollback',
  kicktodoTrustLabel: 'KickTodo Trust',
  kicktodoTrustHint: 'KickTodo trust & operations console',
  kicktodoSafetyLabel: 'KickTodo Safety',
  kicktodoSafetyHint: 'KickTodo safety & approvals queue',
  documentsLabel: 'Documents',
  documentsHint: 'Business documents + templates (SOW, PRD, RFP, agendas)',
  emailLabel: 'Email',
  emailHint: 'Templated campaigns over CRM contacts',
  serviceDeskLabel: "Support",
  serviceDeskHint: "Ticket queue + threads",
  biLabel: "Business metrics",
  biHint: "Governed metric catalog",
  formsLabel: 'Forms',
  formsHint: 'Form builder + submissions',
  kbLabel: 'Knowledge Base',
  kbHint: 'Document collections + semantic search (RAG)',
  marketplaceLabel: 'Marketplace',
  marketplaceHint: 'Feature store, packs & distributions',
  mediaLabel: 'Media',
  mediaHint: 'Org asset library',
  notificationsLabel: 'Inbox',
  notificationsHint: 'What needs you — approvals, blockers, notifications',
  priorityMatrixLabel: 'Priority Matrix',
  priorityMatrixHint: 'Score & rank ideas, plan sessions',
  productionLabel: 'Production',
  productionHint: 'Vendors + AI production planning',
  projectsLabel: 'Projects',
  projectsHint: 'Work containers — board, memory, workflows',
  publishingLabel: 'Publishing',
  publishingHint: 'Public site + SEO for CMS pages',
  scheduledChatsLabel: 'Scheduled chats',
  scheduledChatsHint: 'Recurring agent chats',
  sharingLabel: 'Sharing',
  sharingHint: 'Public share links to pages + collections',
  dashboardLabel: 'Dashboard',
  dashboardHint: 'Your customizable home',
  strategyLabel: 'Strategy',
  strategyHint: 'Define & align company strategy',
  usageAnalyticsLabel: 'LLM usage',
  usageAnalyticsHint: 'Per-model token usage',
  usersLabel: 'Users',
  usersHint: 'Accounts + identity',
  libraryLabel: 'Library',
  libraryHint: 'Everything the AI produced',
  appearanceLabel: 'Appearance',
  appearanceHint: 'Logo, colors, fonts & name for this installation',
  menuSettingsLabel: 'Menu settings',
  menuSettingsHint: 'Customize what shows in each menu',
  auditLogLabel: 'Audit log',
  auditLogHint: 'Every audited action, tenant-scoped',
  eventBindingsLabel: 'Event bindings',
  eventBindingsHint: 'Start a workflow when a record changes',
  heartbeatLabel: 'Heartbeat',
  heartbeatHint: 'Autonomous work-loop cadence + on/off',
  freeTierLabel: "Free tier",
  freeTierHint: "Which provider and model the free tier runs on",
  runtimePostureLabel: "Runtime posture",
  runtimePostureHint: "Warm or cold on Cloud Run, read live",
  dealersLabel: 'Dealers',
  dealersHint: 'Dealer network, outlets & partner registrations',
  commissionsLabel: 'Commissions',
  commissionsHint: 'Rep incentive plans & commission statements',
  salesMapLabel: 'Sales Map',
  salesMapHint: 'Territory attainment & dealer locations on a map',
  territoriesLabel: 'Territories',
  territoriesHint: 'Sales regions, hierarchy, quotas & attainment',
  uiPluginsLabel: 'UI Plugins',
  uiPluginsHint: 'Sandboxed front-end plugin packs',
  claudeCodeLabel: 'Claude Code',
  claudeCodeHint: 'Your paired Claude Code devices',
} as const;
